import { countryConfig } from '../../../country';
import { CL_DATA_SOURCE_ATTRIBUTION } from '../cl/operator';

/** Chile (Liciora) serves its own legal set on these same paths (`content/legal/cl`). */
const IS_CL = countryConfig.code === 'cl';

/**
 * The Spanish legal set's public paths (MiLicita, `es`). Kept apart from the
 * documents themselves so the footer, the cookie banner, the router and the
 * prerender can name the routes without pulling ~40 KB of legal text into the
 * main bundle — the documents load as their own chunk (`routes/legal/es-legal.tsx`).
 */
export const ES_LEGAL_PATHS = {
  avisoLegal: '/aviso-legal',
  privacidad: '/privacidad',
  cookies: '/cookies',
  condiciones: '/condiciones',
} as const;

export type EsLegalKey = keyof typeof ES_LEGAL_PATHS;

/** Prerender / sitemap order. */
export const ES_LEGAL_ROUTES: readonly string[] = [
  ES_LEGAL_PATHS.avisoLegal,
  ES_LEGAL_PATHS.condiciones,
  ES_LEGAL_PATHS.privacidad,
  ES_LEGAL_PATHS.cookies,
];

/** Footer labels — Spanish only, like the documents. Chile says «Términos y condiciones». */
export const ES_LEGAL_LABELS: Record<EsLegalKey, string> = {
  avisoLegal: 'Aviso legal',
  condiciones: IS_CL ? 'Términos y condiciones' : 'Condiciones generales',
  privacidad: 'Política de privacidad',
  cookies: 'Política de cookies',
};

/**
 * The UZ/KZ legal paths the Spanish set REPLACES on an `es` deployment: there
 * they redirect to the Spanish document, are not prerendered and stay out of the
 * sitemap — so the Uzbek оферта / privacy policy and the Uzbek operator's
 * requisites can never render under milicita.es.
 */
export const ES_LEGAL_REPLACES: Readonly<Record<string, string>> = {
  '/offer': ES_LEGAL_PATHS.condiciones,
  '/privacy': ES_LEGAL_PATHS.privacidad,
  '/requisites': ES_LEGAL_PATHS.avisoLegal,
};

/**
 * Does this deployment ship the Spanish-language legal set? Keyed on the country
 * CODE (a string compare). Spain serves its own documents, Chile its Chilean ones
 * (`content/legal/cl`) on the same paths. UZ and KZ answer `false` and keep
 * `/offer`, `/privacy`, `/requisites` exactly as they are.
 */
export function hasEsLegalSet(countryCode: string): boolean {
  return countryCode === 'es' || countryCode === 'cl';
}

/**
 * The terms the site's public data is published under — the JSON-LD `license`
 * of the company and price Datasets. UZ/KZ: the оферта (`/offer`), unchanged.
 * Spain: «Condiciones generales» — `/offer` only redirects there
 * ({@link ES_LEGAL_REPLACES}), and a `license` should name the page itself.
 */
export function termsPath(countryCode: string): string {
  return hasEsLegalSet(countryCode) ? ES_LEGAL_PATHS.condiciones : '/offer';
}

/**
 * Data-source attribution required by the reuse conditions of the Ministerio de
 * Hacienda (its aviso legal, «Condiciones generales para la reutilización», read
 * 2026-10-19): «Debe citarse la fuente de los documentos objeto de la
 * reutilización. Esta cita podrá realizarse de la siguiente manera: "Origen de
 * los datos: Ministerio de Hacienda"», and «No se podrá indicar, insinuar o
 * sugerir que este Ministerio […] participa, patrocina o apoya la reutilización».
 * The PLACSP portal's own aviso legal authorises reproduction «siempre que se
 * cite expresamente su origen». Shown in the footer of every page.
 *
 * 2026-10-22 (content package audit ES-08): the last sentence used to say that
 * «solo la publicación en el perfil de contratante produce efectos jurídicos». It
 * does not: a notification has legal effect too — the LCSP counts deadlines from it
 * (DA 15.ª; claim ES-C057) and Ley 39/2015 deems an unopened one rejected after ten
 * natural days (art. 43.2; ES-C056). The package's wording replaces it here, in
 * Condiciones §10 and in the Aviso legal §4.
 */
const ES_SPAIN_DATA_SOURCE_ATTRIBUTION =
  'Origen de los datos: Plataforma de Contratación del Sector Público, Ministerio de Hacienda. ' +
  'MiLicita es un servicio independiente: el Ministerio no participa en él, ni lo patrocina ni lo ' +
  'apoya. La información tiene carácter informativo y no oficial: verifica en la fuente oficial ' +
  'los pliegos, las modificaciones, los plazos y las notificaciones. La presentación de la oferta ' +
  'y las actuaciones con efectos jurídicos se realizan por los medios indicados oficialmente para ' +
  'cada procedimiento.';

/** The footer's data-source line: Spain's above; on Chile, Mercado Público's (`cl/operator.ts`). */
export const ES_DATA_SOURCE_ATTRIBUTION: string = IS_CL
  ? CL_DATA_SOURCE_ATTRIBUTION
  : ES_SPAIN_DATA_SOURCE_ATTRIBUTION;
